import { Trans, useTranslation } from 'react-i18next'
import Page from '../../../Components/Page'
import { GUEST_EVENTS_LEAD_FEED, HEADBOX_ROOT_SERVICE } from '../../../Constants'
import { PartialBrief } from '../../../Types/Brief'
import getGuestEventsURL from '../getGuestEventsURL/getGuestEventsURL'
import { EnvelopeIconSVG, HeartIconSVG, TickIconSVG } from './SuccessPageIcons'
import useLocale from '../../../Hooks/Locale/useLocale'
import { ActiveLocale } from '../../../Types/Location'
import useSpaceRecommendations from '../../../Hooks/useRecommendation/useSpaceRecommendations'
import './LeadFeedSuccess.less'
import '../../../Styles/buttons.less'
import Spinner from '../../../Components/LoadingSpinner'
import { CrossIcon, GroupIcon, HeartIcon, LeftIcon, LocationIcon, RightIcon } from './Icons'
import { useEffect, useState } from 'react'
import { useCreateFavourites } from '../../../Hooks/Brief/useCreateFavorites/useCreateFavorites'
import useBriefContext from '../../../Hooks/Brief/useBriefContext'
import getBriefUserAttributes from '../../../Utilities/User/getBriefUserAttributes'
import { useGlobalAuth } from '@headbox-ui/auth-hook'

const getHomepageURL = (locale: ActiveLocale): URL => {
  const homepageURL = new URL(HEADBOX_ROOT_SERVICE)
  if (['US', 'AU'].includes(locale)) homepageURL.pathname = locale.toLowerCase()

  return homepageURL
}

const SuccessLink = ({ secret }: { secret: string | null }): JSX.Element => {
  const { t } = useTranslation('successPage')
  const locale = useLocale()
  const homepageURL = getHomepageURL(locale)

  return secret ? (
    <a
      className='hb-button primary-button'
      href={getGuestEventsURL({ showSuccess: false }).toString()}>
      {t('page.gexp-link-text')}
    </a>
  ) : (
    <a className='home-page-link' href={homepageURL.toString()}>
      Return to homepage
    </a>
  )
}

const SuccessHeader = ({ guestEmail }: { guestEmail?: string }): JSX.Element => {
  const { t } = useTranslation('successPage')
  return (
    <header className='success-header'>
      <div className='success-header-content'>
        <h1>
          <Trans
            i18nKey={'successPage:header.h1'}
            values={{ srOnlyText: t('header.h1-sr-only-text') }}
            components={{ srOnly: <span className='visually-hidden' /> }}
          />
        </h1>
        <p>
          We’ve sent a confirmation to{' '}
          <span className={guestEmail ? 'email' : ''}>{guestEmail ?? 'your email address.'}</span>
        </p>
      </div>
    </header>
  )
}

const Carousel = ({ images, alt }: { images: string[]; alt: string }): JSX.Element | null => {
  const [currentIndex, setCurrentIndex] = useState(0)
  return (
    <div className='space-recs-image-carousel'>
      <img src={images[currentIndex]} alt={`${alt} - ${currentIndex + 1}`} />
      {images.length > 1 && (
        <div className='space-recs-image-carousel-nav'>
          <button
            className='space-recs-image-carousel-nav-prev'
            onClick={() => setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1))}
            aria-label='Previous image'>
            <LeftIcon height={32} width={32} />
          </button>
          <button
            className='space-recs-image-carousel-nav-next'
            onClick={() => setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1))}
            aria-label='Next image'>
            <RightIcon height={32} width={32} />
          </button>
        </div>
      )}
    </div>
  )
}

const SpaceRecs = (): JSX.Element | null => {
  const { user } = useGlobalAuth()
  const { brief, dispatchBrief } = useBriefContext()
  const { enquiryAccessSecret, createFavourites } = useCreateFavourites(brief.id)
  const { loading, recommendations } = useSpaceRecommendations(brief)
  const [page, setPage] = useState(1)
  const [requestedSpaces, setRequestedSpaces] = useState<
    {
      spaceId: number
      hostId: number
    }[]
  >([])
  const [rejectedSpaces, setRejectedSpaces] = useState<number[]>([])

  useEffect(() => {
    const resetBrief = () => {
      dispatchBrief({
        overwrite: {
          ...getBriefUserAttributes(user, { newlyRegistered: false }),
        },
      })
    }

    window?.addEventListener('beforeunload', resetBrief)

    return () => {
      window?.removeEventListener('beforeunload', resetBrief)
    }
  }, [])

  useEffect(() => {
    if (recommendations.length) {
      setRequestedSpaces(
        recommendations.slice(0, 5).map((space) => ({
          spaceId: Number(space.id),
          hostId: space.hostID,
        }))
      )
    }
  }, [recommendations])

  if (loading) {
    return <Spinner />
  }

  if (!recommendations.length) {
    return null
  }

  return (
    <>
      <div className='space-recs-header'>
        <h2>Finally, request quotes from venues you like - no strings attached!</h2>
      </div>
      <div className='space-recs'>
        {recommendations.slice(0, page * 6).map((space) => {
          const spaceId = Number(space.id)
          const isSelected = requestedSpaces.some(
            (requestedSpace) => requestedSpace.spaceId === spaceId
          )
          const isRejected = rejectedSpaces.some((requestedSpace) => requestedSpace === spaceId)

          return (
            <div key={space.id} className='space-recs-card'>
              <Carousel images={space.images} alt={`${space.title}, ${space.venueName}`} />
              <div className='space-recs-card-content'>
                {!!space.tags.length && (
                  <div className='space-recs-card-tags'>
                    {space.tags.map((tag) => (
                      <div
                        key={tag}
                        className={`space-recs-card-tag${
                          tag === 'Recommended' ? ' space-recs-card-tag-recommended' : ''
                        }`}>
                        {tag}
                      </div>
                    ))}
                  </div>
                )}
                <h3 className='space-recs-card-clamp'>
                  {space.title}, {space.venueName}
                </h3>
                <div className='space-recs-card-details'>
                  <p className='space-recs-card-text-md'>
                    <LocationIcon height={20} width={20} className='space-recs-icon' />{' '}
                    {space.location.city} {space.location.country}
                  </p>
                  {space.capacity && (
                    <p className='space-recs-card-text-md'>
                      <GroupIcon height={18} width={18} className='space-recs-icon' />{' '}
                      {space.capacity}
                    </p>
                  )}
                </div>
                <p className='space-recs-card-clamp'>{space.description}</p>
                <div className='space-recs-card-actions'>
                  <button
                    onClick={() => {
                      if (
                        requestedSpaces.some((requestedSpace) => requestedSpace.spaceId === spaceId)
                      ) {
                        setRequestedSpaces((prev) =>
                          prev.filter((requestedSpace) => requestedSpace.spaceId !== spaceId)
                        )
                        setRejectedSpaces((prev) => [...prev, spaceId])
                      } else {
                        setRequestedSpaces((prev) => [
                          ...prev,
                          {
                            spaceId,
                            hostId: space.hostID,
                          },
                        ])
                        setRejectedSpaces((prev) =>
                          prev.filter((rejectedSpace) => rejectedSpace !== spaceId)
                        )
                      }
                    }}
                    className={`hb-button button-small ${
                      isSelected ? 'primary-button' : 'secondary-button'
                    }`}>
                    <HeartIcon height={18} width={18} />
                    I&apos;m interested
                  </button>
                  <button
                    onClick={() => {
                      setRequestedSpaces((prev) =>
                        prev.filter((requestedSpace) => requestedSpace.spaceId !== spaceId)
                      )
                      setRejectedSpaces((prev) => [...prev, spaceId])
                    }}
                    className={`hb-button button-small secondary-button${
                      isRejected ? ' space-recs-card-rejected' : ''
                    }`}>
                    <CrossIcon height={18} width={18} />
                    Not for me
                  </button>
                </div>
              </div>
            </div>
          )
        })}
      </div>
      <button
        className='hb-button button-small secondary-button'
        onClick={() => setPage(page + 1)}
        disabled={page * 6 >= recommendations.length}>
        + Load more
      </button>
      <nav className='recs-nav'>
        <menu className='recs-nav-actions' role={'list'}>
          <li>
            <button
              className='hb-button button-small primary-button'
              onClick={async () => {
                if (requestedSpaces.length) {
                  await createFavourites(requestedSpaces)
                }

                dispatchBrief({
                  overwrite: {
                    ...getBriefUserAttributes(user, { newlyRegistered: false }),
                  },
                })

                window.location.href = `${GUEST_EVENTS_LEAD_FEED}?accessSecret=${enquiryAccessSecret}`
              }}
              disabled={!enquiryAccessSecret}>
              {requestedSpaces.length ? 'Request quotes & view enquiry' : 'View my enquiry'}
            </button>
          </li>
        </menu>
      </nav>
    </>
  )
}

interface LeadFeedSuccessPageProps {
  directEnquiry: boolean
  directEnquiryVenueName: string | null
  brief: PartialBrief
  messageLimit?: number
}

const LeadFeedSuccessPage = ({
  directEnquiry,
  directEnquiryVenueName,
  brief,
  messageLimit,
}: LeadFeedSuccessPageProps) => {
  const { t } = useTranslation('successPage')
  return (
    <Page
      metaTitle='All done! - HeadBox'
      className='success-page fade-in'
      header={<SuccessHeader guestEmail={brief?.guest?.email} />}>
      <SpaceRecs />
      <div className='success-card'>
        <h2>What happens next?</h2>
        <section className='card-section'>
          <h3 className='card-section-header'>
            <span className='icon-wrap'>
              <TickIconSVG className='card-section-icon' aria-hidden='true' />
              Sit back and relax
            </span>
          </h3>
          <p className='card-text'>
            {directEnquiry &&
              !!directEnquiryVenueName &&
              t('page.step-1.paragraph.named-venue', {
                venue: directEnquiryVenueName,
                interpolation: { escapeValue: false },
              })}
            {directEnquiry && !directEnquiryVenueName && t('page.step-1.paragraph.direct-enquiry')}
            {!directEnquiry && t('page.step-1.paragraph.standard')}
          </p>
        </section>
        <section className='card-section'>
          <h3 className='card-section-header'>
            <span className='icon-wrap'>
              <EnvelopeIconSVG className='card-section-icon' aria-hidden='true' />
              {messageLimit && <>Get messages from up to {messageLimit} venues</>}
              {!messageLimit && <>Get messages from venues</>}
            </span>
          </h3>
          <p className='card-text'>
            We’ll email you once messages start coming in from available venues.
          </p>
        </section>
        <section className='card-section'>
          <h3 className='card-section-header'>
            <span className='icon-wrap'>
              <HeartIconSVG className='card-section-icon' aria-hidden='true' />
              {t('page.step-3.heading')}
            </span>
          </h3>
          <p className='card-text'>
            Review your options, match with venues and they’ll be in touch directly to discuss
            things further.
          </p>
        </section>
        <SuccessLink secret={null} />
      </div>
    </Page>
  )
}

export default LeadFeedSuccessPage
